'use client';

import { useState, useTransition } from 'react';

import {
  followProfileAction,
  unfollowProfileAction,
} from '@kit/artists/server';
import { Button } from '@kit/ui/button';
import { toast } from '@kit/ui/sonner';

interface FollowButtonProps {
  /** The dancer's `auth.users.id` (= `profiles.id`). Passed straight to
   * the canonical `followProfileAction`, whose resolver treats a UUID as
   * a `user_id` directly. NOT the professional_profile.id. */
  profileUserId: string;
  initialIsFollowing: boolean;
}

/**
 * Follow toggle for the public dancer page contact rail. Optimistic
 * update with rollback on failure. Surfaces server-action errors via
 * toast so the user knows what went wrong (sign-in needed, no profile,
 * etc.).
 *
 * Writes to the canonical `profile_follows` table via `@kit/artists`
 * `followProfileAction` / `unfollowProfileAction` — the SAME table the
 * public profile reads follow-state from. (Previously imported the
 * `@kit/community` pair, which wrote the legacy polymorphic `follows`
 * table, so the state never round-tripped: split-brain fixed in
 * WIP_consolidating_dancer_follow_save_to_canonical_tables_2026_05_30.)
 *
 * The artists actions return `{ success: true }` or THROW on failure
 * (no `{ success:false }` envelope), so error handling lives entirely
 * in the catch.
 */
export function FollowButton({
  profileUserId,
  initialIsFollowing,
}: FollowButtonProps) {
  const [isFollowing, setIsFollowing] = useState(initialIsFollowing);
  const [isPending, startTransition] = useTransition();

  const handleClick = () => {
    const wasFollowing = isFollowing;
    setIsFollowing(!wasFollowing);

    startTransition(async () => {
      try {
        if (wasFollowing) {
          await unfollowProfileAction(profileUserId);
        } else {
          await followProfileAction(profileUserId);
        }
      } catch (error) {
        setIsFollowing(wasFollowing);
        toast.error(
          error instanceof Error
            ? error.message
            : wasFollowing
              ? 'Failed to unfollow'
              : 'Failed to follow',
        );
      }
    });
  };

  return (
    <Button
      variant="editorial"
      className="h-11 w-full"
      onClick={handleClick}
      disabled={isPending}
      aria-pressed={isFollowing}
    >
      {isFollowing ? 'Following ✓' : 'Follow'}
    </Button>
  );
}
